<template lang="pug">
.c-help(v-if="links.length > 0")
  .c-help__header
    h2.c-help__title Need Help Getting Started?
    p.c-help__subtitle Explore our resources to maximize your impact
  .c-help__grid
    a.c-help__tile(
      v-for="link in links"
      :key="link.id"
      :href="link.url"
      target="_blank"
      rel="noopener noreferrer"
      @click="opened(link)"
    )
      .c-help__icon
        fai(type="fal" :icon="iconFor(link.icon)")
      h3.c-help__tile-title {{ link.title }}
      p.c-help__tile-text(v-if="link.description") {{ link.description }}
  .c-help__ask
    p.c-help__ask-title Still stuck? Ask us a question
    help-question-box(:org-id="orgId" start-open)
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import globalShared from '@/shared';
import { findHelpLinks } from '@/graphs/gql';
import HelpQuestionBox from './HelpQuestionBox.vue';
import { trackHelp } from './useSetupChecklist';

interface HelpLink {
  id: string;
  title: string;
  description: string | null;
  url: string;
  icon: string | null;
}

const ICONS: { [name: string]: string } = {
  rocket: 'rocket',
  lightbulb: 'lightbulb',
  'help-circle': 'circle-question',
  'book-open': 'book-open',
  'message-circle': 'comments',
};

export default defineComponent({
  name: 'HelpLinksSection',
  components: { HelpQuestionBox },
  props: {
    placement: { type: String, default: 'DASHBOARD' },
    orgId: { type: String, default: null },
  },
  setup(props) {
    const { makeGraphRequest } = globalShared();
    const route = useRoute();
    const links = ref<HelpLink[]>([]);

    async function load() {
      try {
        const response = await makeGraphRequest(
          'query',
          findHelpLinks,
          { placement: props.placement },
        );
        links.value = response?.errors ? [] : response?.data?.findHelpLinks || [];
      } catch (e) {
        links.value = [];
      }
    }
    watch(() => props.placement, load, { immediate: true });

    const iconFor = (name: string | null) => (name && ICONS[name]) || 'circle-question';
    function opened(link: HelpLink) {
      if (!props.orgId) return;
      trackHelp({
        eventType: 'HELP_LINK_OPENED', helpLinkId: link.id, orgId: props.orgId, page: route.path,
      });
    }
    return { links, iconFor, opened };
  },
});
</script>

<style lang="scss" scoped>
$ui-font: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue',
  Arial, sans-serif;

.c-help {
  margin-top: 16px;
  padding: 32px;
  border-radius: 14px;
  font-family: $ui-font;
  background: linear-gradient(135deg, #e8e9fd, #dde9f9);
  @media (max-width: 768px) { padding: 20px 16px; }
  &__header { text-align: center; margin-bottom: 24px; }
  &__title {
    margin: 0 0 8px;
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    color: #111827;
  }
  &__subtitle { margin: 0; font-size: 16px; line-height: 24px; color: #4b5563; }
  &__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 16px; }
  &__tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px;
    border-radius: 8px;
    background: #fff;
    text-align: center;
    text-decoration: none;
    transition: box-shadow 0.2s;
    &:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
      .c-help__icon { transform: scale(1.1); }
    }
    &:nth-child(5n + 2) .c-help__icon { background: #fef7e0; color: #8a7800; }
    &:nth-child(5n + 3) .c-help__icon { background: #ffe0ee; color: #ff6b9d; }
    &:nth-child(5n + 4) .c-help__icon { background: #dde9f9; color: #4876c9; }
    &:nth-child(5n + 5) .c-help__icon { background: #e8e9fd; color: #4349dd; }
  }
  :deep(svg) { width: 1em; height: 1em; }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #c3eae3;
    color: #005c6b;
    font-size: 24px;
    transition: transform 0.2s;
  }
  &__tile-title {
    margin: 0 0 -8px;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: #111827;
  }
  &__tile-text { margin: 0; font-size: 12px; line-height: 16px; color: #4b5563; }
  &__ask { max-width: 520px; margin: 24px auto 0; text-align: center; }
  &__ask-title { margin: 0 0 8px; font-size: 14px; font-weight: 600; color: #111827; }
}
</style>
